import { latestEntry, runningEntry } from '@/lib/newBuild';
import { remove, writeJson } from '@/lib/storage';
import { resolveTenant } from '@/lib/tenant';
/**
 * Registering the service worker.
 *
 * **On every load, after first paint** (`main.tsx`). Offline is the product —
 * a visitor who opens a rave on the bus and loses signal at the gate must
 * still have the app — so there is no "register once they attend" step to
 * forget. What the worker precaches on install is the VISITOR's shell only
 * (`swPlugin.ts` derives it from the chunk graph; the lazy admin chunks are
 * left out), so registering costs a visitor the app they are already using.
 * A rave's photos and map are a separate download (`OfflineProvider`).
 *
 * **A new version takes over at once**: the worker calls `skipWaiting()` on
 * install and `clients.claim()` on activate (`sw.ts`). The shell cache is named
 * by a content hash, so the new worker's cache is complete before it activates
 * and the old one is dropped only then.
 *
 * **And the page follows it — but never under somebody's thumb** (the user,
 * 2026-10-07: three times in a day a phone opened yesterday's app from its
 * stored copy and looked like a failed deploy). When a NEW worker takes
 * control (`controllerchange`, and there was one before — a first install
 * changes nothing on screen):
 * - within `FRESH_MS` of the page loading, nobody is using it yet: reload now;
 * - later, wait until the page is hidden (the phone locked, the app left) and
 *   reload then, so it comes back as the new version.
 * Once per page: after the reload the new worker is already in control.
 *
 * **Only when the page IS the old version** (the user, 2026-10-27: "I saw the
 * home screen, then a loading screen, then the home screen again" — on every
 * start after a deploy). The page is fetched from the network first, so it is
 * usually the newest already; the new worker installs after it and takes
 * over a moment later, and reloading THEN loaded the same page twice. So the
 * page compares the build it runs with the server's (`newBuild.ts`) and
 * reloads only on a difference — or when that cannot be asked (no signal).
 */
const FRESH_MS = 15_000;

/** Is the build on screen older than the server's? Unknown (no signal, no hashed entry) counts as yes. */
async function pageIsStale(): Promise<boolean> {
  const running = runningEntry();
  if (!running) return true;
  const latest = await latestEntry();
  return latest === null || latest !== running;
}

/**
 * **Only a rave keeps the app for offline — everywhere** (the user, 2026-10-30: "only the rave app needs offline, the
 * rest is not relevant; the main app in offline mode will not work, it only lets you into the active rave"; "yes,
 * everywhere"; tickets are held by the rave app, the main app only reads them). Found on the user's iPhone over the
 * cable the same day: an app that runs workers through app-bound domains may hold at most THREE registrations
 * (WebKit `SWServer.cpp`, `defaultMaxRegistrationCount = 3`; past it, "Job rejected for non app-bound domain"), one
 * per address — `login.`, `account.` and `support.` had taken them, so no rave kept anything. Now the iPhone's three
 * are three raves. Every other address (`app.`, the sign-in, account, support, the consoles, the public site)
 * registers nothing and gives back a worker it holds from before.
 */
export function keepsWorkerHere(hostname: string): boolean {
  return resolveTenant(hostname).subdomain !== null;
}

/**
 * At every load (`main.tsx`): an address that never keeps the app gives back a worker it holds. A RAVE's own
 * address decides once the account and its raves are known (`RaveOffline`, `raveSlots.ts`) — offline is for an
 * account, by the rave's dates (the user, 2026-10-30).
 */
export async function prepareServiceWorker(): Promise<void> {
  if (!('serviceWorker' in navigator)) return;
  if (import.meta.env.DEV) return; // the dev server serves fresh anyway
  if (!keepsWorkerHere(window.location.hostname)) await dropWorker();
}

/** This address keeps nothing for offline: its worker and its caches go — the slot is given back. */
export async function dropWorker(): Promise<void> {
  try { for (const r of await navigator.serviceWorker.getRegistrations()) await r.unregister(); } catch { /* none to free */ }
  try {
    if (typeof caches !== 'undefined') for (const k of await caches.keys()) if (k.startsWith('myrave-')) await caches.delete(k);
  } catch { /* nothing kept */ }
}

/** Keep the app here for offline. `rejected`: the browser refused it (the iPhone app's three are taken). */
export async function registerServiceWorker(): Promise<{ reg: ServiceWorkerRegistration | null; rejected: boolean }> {
  if (!('serviceWorker' in navigator) || import.meta.env.DEV) return { reg: null, rejected: false };
  followNewVersions();
  try {
    const reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
    watchInstall(reg);
    return { reg, rejected: false };
  } catch (e) {
    // A blocked worker means no offline support, not a broken app — but it is SAID, on the version page.
    noteWorkerProblem(`register: ${e instanceof Error ? `${e.name}: ${e.message}` : String(e)}`);
    return { reg: null, rejected: true };
  }
}

/**
 * Why the app is not kept for offline, as the browser said it (the user, 2026-10-30: the iPhone app showed "Not kept
 * for offline, Files kept: 0" with no reason anywhere — every failure here was swallowed). The version page's
 * "Offline app" row shows it; a worker that takes over clears it. Platform-wide: one worker per origin, one note.
 */
export const WORKER_PROBLEM = 'sw-problem';
export function noteWorkerProblem(message: string): void {
  writeJson(null, WORKER_PROBLEM, { message: message.slice(0, 200), at: Date.now() });
}

/** Exported for the test: an install that ends `redundant` failed; one that activates is fine. */
export function watchInstall(reg: Pick<ServiceWorkerRegistration, 'installing' | 'active'>): void {
  if (reg.active) remove(null, WORKER_PROBLEM);
  const w = reg.installing;
  if (!w) return;
  w.addEventListener('statechange', () => {
    if (w.state === 'redundant') noteWorkerProblem('install: the worker was dropped (a file it keeps could not be fetched)');
    if (w.state === 'activated') remove(null, WORKER_PROBLEM);
  });
}

/** Exported for the test. */
export function followNewVersions(
  loadedAt = performance.now(),
  reload = () => window.location.reload(),
  stale: () => Promise<boolean> = pageIsStale,
): void {
  const sw = navigator.serviceWorker;
  const hadController = Boolean(sw.controller);
  let done = false;
  sw.addEventListener('controllerchange', async () => {
    if (!hadController || done) return;
    done = true;
    if (!(await stale())) return;
    if (performance.now() - loadedAt < FRESH_MS || document.visibilityState === 'hidden') { reload(); return; }
    const onHide = () => {
      if (document.visibilityState !== 'hidden') return;
      document.removeEventListener('visibilitychange', onHide);
      reload();
    };
    document.addEventListener('visibilitychange', onHide);
  });
}
